import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, LayoutGrid, VideoOff } from "lucide-react";
import { Header } from "../components/Header";
import { CameraCard } from "../components/CameraCard";
import { camerasApi } from "../api";
import { Camera, demoCameras } from "../types";
import styles from "./Cameras.module.css";

export function CamerasPage() {
  const navigate = useNavigate();
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [addressFilter, setAddressFilter] = useState("");
  const [objectFilter, setObjectFilter] = useState("");
  const [installationFilter, setInstallationFilter] = useState<string>("all");

  useEffect(() => {
    fetchCameras();
  }, []);

  const fetchCameras = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await camerasApi.getAll();
      console.log('Cameras fetched:', data);
      setCameras(data);
    } catch (err) {
      console.error("Failed to fetch cameras, using demo data:", err);
      // Fallback to demo data when API is unavailable
      setCameras(demoCameras);
      setError("Демо-режим: используются тестовые данные");
    } finally {
      setLoading(false);
    }
  };

  const filteredCameras = useMemo(() => {
    return cameras.filter((camera) => {
      const attrs = camera;
      // Search filter
      const searchLower = search.toLowerCase();
      const matchesSearch =
        !search ||
        attrs.name.toLowerCase().includes(searchLower) ||
        attrs.address?.toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus =
        statusFilter === "all" || attrs.cameraStatus === statusFilter;

      // Address filter
      const matchesAddress =
        !addressFilter ||
        attrs.address?.toLowerCase().includes(addressFilter.toLowerCase());

      // Object filter
      const matchesObject =
        !objectFilter ||
        attrs.objectName?.toLowerCase().includes(objectFilter.toLowerCase());

      // Installation type filter
      const matchesInstallation =
        installationFilter === "all" ||
        attrs.installationType === installationFilter;

      return (
        matchesSearch &&
        matchesStatus &&
        matchesAddress &&
        matchesObject &&
        matchesInstallation
      );
    });
  }, [
    cameras,
    search,
    statusFilter,
    addressFilter,
    objectFilter,
    installationFilter,
  ]);

  const stats = useMemo(() => {
    const total = cameras.length;
    const active = cameras.filter((c) => c.cameraStatus === "active").length;
    const inactive = total - active;
    return { total, active, inactive };
  }, [cameras]);

  const handleCameraClick = (documentId: string) => {
    navigate(`/cameras/${documentId}`);
  };

  const handleGridClick = () => {
    navigate("/grid");
  };

  const handleLogout = () => {
    navigate("/login");
  };

  const handleRefresh = () => {
    fetchCameras();
  };

  return (
    <div className={styles.page}>
      <Header
        currentPage="cameras"
        onNavigate={handleGridClick}
        onLogout={handleLogout}
        onRefresh={handleRefresh}
      />

      <main className={styles.main}>
        <section className={styles.filtersSection}>
          <div className={styles.filtersRow}>
            <div className={styles.searchWrapper}>
              <Search size={18} className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Поиск по названию или адресу..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select
              className={styles.filterSelect}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Фильтр по статусу"
            >
              <option value="all">Все статусы</option>
              <option value="active">Активные</option>
              <option value="inactive">Неактивные</option>
            </select>

            <input
              type="text"
              className={styles.filterInput}
              placeholder="Адрес"
              value={addressFilter}
              onChange={(e) => setAddressFilter(e.target.value)}
            />

            <input
              type="text"
              className={styles.filterInput}
              placeholder="Объект"
              value={objectFilter}
              onChange={(e) => setObjectFilter(e.target.value)}
            />

            <select
              className={styles.filterSelect}
              value={installationFilter}
              onChange={(e) => setInstallationFilter(e.target.value)}
              aria-label="Фильтр по типу установки"
            >
              <option value="all">Все типы</option>
              <option value="roof">Крыша</option>
              <option value="facade">Фасад</option>
              <option value="cabin">Бокс</option>
            </select>

            <button className={styles.gridButton} onClick={handleGridClick}>
              <LayoutGrid size={18} />
              Сетка
            </button>
          </div>

          <div className={styles.statsRow}>
            <div className={styles.statsItem}>
              <span className={styles.statsLabel}>Всего:</span>
              <span className={styles.statsValue}>{stats.total}</span>
            </div>
            <div className={styles.statsItem}>
              <span className={styles.statsLabel}>Активных:</span>
              <span
                className={`${styles.statsValue} ${styles.statsValueActive}`}
              >
                {stats.active}
              </span>
            </div>
            <div className={styles.statsItem}>
              <span className={styles.statsLabel}>Неактивных:</span>
              <span
                className={`${styles.statsValue} ${styles.statsValueInactive}`}
              >
                {stats.inactive}
              </span>
            </div>
          </div>
        </section>

        {error && <div className={styles.errorBanner}>{error}</div>}

        <section className={styles.grid}>
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <span>Загрузка камер...</span>
            </div>
          ) : filteredCameras.length > 0 ? (
            filteredCameras.map((camera) => (
              <CameraCard
                key={camera.documentId}
                id={String(camera.id)}
                documentId={camera.documentId}
                name={camera.name}
                address={camera.address || "Не указан"}
                object={camera.objectName || "Не указан"}
                status={camera.cameraStatus}
                streamUrl={camera.streamUrl}
                onClick={handleCameraClick}
              />
            ))
          ) : (
            <div className={styles.emptyState}>
              <VideoOff size={48} className={styles.emptyIcon} />
              <h3 className={styles.emptyTitle}>Камеры не найдены</h3>
              <p className={styles.emptyText}>
                Попробуйте изменить параметры поиска или сбросить фильтры
              </p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
